import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import Modal from '@component/common/modal';
import { useAuth } from '@context/auth-context';
import { useAppStore } from '@store/app-store';
import { useToast } from '@context/toast-context';
import { STORAGE_KEYS } from '@config/storageKeys';
import { saveJson } from '@lib/storage';
import { SEED } from '@/data';
import { enrichCoeTrainingRow, normalizeC2TrainingType } from '@lib/training';
import { PLACEMENT_EMPLOYMENT_STATUS_OPTIONS, normalizePlacementEmploymentStatus } from '@lib/trainingPlacementImportExport';
import {
  buildCmRowFromProc,
  isProcGoodsType,
  isProcServicesType,
  mergeCmRow,
  procCmCategory,
  procHistoryActor,
  packageActivityHistory,
  PROC_STATUS,
  resolveProcStoreRow,
  stripCmRowMeta,
} from '@lib/procurement';
import { CM_INSURANCE_TYPES, fmtCmInsuranceExpiry, isCmBankGuaranteeType, normalizeCmGoodsRow, normalizeCmSvcRow, resolveCmStoreRow } from '@lib/cmServices';
import CmPerfSecurityFields from '@feature/contract-mgmt/components/cm-perf-security-fields';
import CmServicesFormSection from '@feature/contract-mgmt/components/cm-services-form-section';
import ProcPackageDetailView from '@feature/procurement/components/proc-package-detail-view';
import ProcActivityHistorySection from '@feature/procurement/components/proc-activity-history';
import {
  isC3PaymentLocked,
  c3ExpenseHeadOptions,
  c3RecalcExpense,
  projectC3ContingencyStats,
  projectC3ProvisionalSumStats,
  formatPkr,
} from '@lib/c3';
import { GAP_COLUMNS, GAP_STATUSES, normalizeGapStatus } from '@lib/gap';
import {
  asProcRow,
  buildGenderCounts,
  formDeliverables,
  formField,
  formMonths,
  formNum,
  formStr,
  parseOptionalCount,
  type ModalFormState,
} from '@app/modal-form-types';
import type { C3FormRow } from '@app/modal-form-types';
import type { ProcListKey, TrainingRow } from '@type/store';

const DATA_VERSION = 10;

function asC3Row(row: Record<string, unknown> | undefined | null): C3FormRow {
  return (row ?? {});
}

function Field({
  label,
  children,
  className = '',
  style,
}: {
  label: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={`fg2 ${className}`} style={style}>
      <label>{label}</label>
      {children}
    </div>
  );
}

function FormFooter({
  onClose,
  onSave,
  audit,
}: {
  onClose: () => void;
  onSave?: () => void;
  audit?: string;
}) {
  return (
    <div className="de-form-footer">
      {audit && <span className="audit-pill">{audit}</span>}
      <button type="button" className="btn-secondary" onClick={onClose}>{onSave ? 'Cancel' : 'Close'}</button>
      {onSave && (
        <button type="button" className="btn-submit" onClick={onSave}>Save & Update Dashboard</button>
      )}
    </div>
  );
}

export default function ModalForms() {
  const { user } = useAuth();
  const modal = useAppStore((s) => s.modal);
  const contractMgmt = useAppStore((s) => s.contractMgmt);
  const procYetToInitiate = useAppStore((s) => s.procYetToInitiate);
  const procInprocess = useAppStore((s) => s.procInprocess);
  const procOngoing = useAppStore((s) => s.procOngoing);
  const procCompleted = useAppStore((s) => s.procCompleted);
  const procCivil = useAppStore((s) => s.procCivil);
  const component3 = useAppStore((s) => s.component3);
  const civil = useAppStore((s) => s.civil);
  const closeModal = useAppStore((s) => s.closeModal);
  const updateCivil = useAppStore((s) => s.updateCivil);
  const saveGap = useAppStore((s) => s.saveGap);
  const saveBudgetRow = useAppStore((s) => s.saveBudgetRow);
  const saveTrainingRow = useAppStore((s) => s.saveTrainingRow);
  const addProcPackage = useAppStore((s) => s.addProcPackage);
  const pushProcHistory = useAppStore((s) => s.pushProcHistory);
  const saveProcYetToInitiate = useAppStore((s) => s.saveProcYetToInitiate);
  const promoteYtiToInProcess = useAppStore((s) => s.promoteYtiToInProcess);
  const demoteInProcessToYti = useAppStore((s) => s.demoteInProcessToYti);
  const saveProcInprocess = useAppStore((s) => s.saveProcInprocess);
  const saveContractMgmt = useAppStore((s) => s.saveContractMgmt);
  const saveC3DeliverableDetail = useAppStore((s) => s.saveC3DeliverableDetail);
  const saveC3DeliverablePayment = useAppStore((s) => s.saveC3DeliverablePayment);
  const saveC3Expense = useAppStore((s) => s.saveC3Expense);
  const saveC3Contingency = useAppStore((s) => s.saveC3Contingency);
  const saveC3ProvisionalSum = useAppStore((s) => s.saveC3ProvisionalSum);
  const addAudit = useAppStore((s) => s.addAudit);
  const setProcModTab = useAppStore((s) => s.setProcModTab);
  const { showToast } = useToast();
  const [form, setForm] = useState<ModalFormState>({});

  useEffect(() => {
    if (!modal) {
      setForm({});
      return;
    }
    if (modal.type === 'civil' && modal.row) {
      setForm({ ...modal.row });
    } else if (modal.type?.startsWith('c3-') && modal.row) {
      setForm(JSON.parse(JSON.stringify(modal.row)));
    } else if (modal.type === 'cm' && modal.row) {
      const cat = modal.cat || 'coe';
      const { row: storeRow } = resolveCmStoreRow(
        contractMgmt,
        cat,
        modal.row,
        modal.index,
      );
      if (cat === 'cons' || cat === 'nc') {
        setForm(normalizeCmSvcRow(storeRow));
      } else {
        setForm(JSON.parse(JSON.stringify(storeRow)));
      }
    } else if (modal.type === 'cm-insurance' && modal.row) {
      const cat = modal.cat || 'cons';
      const { row: storeRow } = resolveCmStoreRow(
        contractMgmt,
        cat,
        modal.row,
        modal.index,
      );
      setForm(normalizeCmSvcRow(storeRow));
    } else if (modal.type === 'procurement') {
      setForm({
        ref: '',
        name: '',
        type: 'Goods',
        fy: '2025-26',
        party: '',
        pkr: '',
        usd: '',
        status: PROC_STATUS.YET,
        dateOfEntry: '',
        remarks: '',
        advertisedOn: '',
        ppRef: '',
        bidOpenDate: '',
        bidValidityDays: '',
        bidValidityTill: '',
        firmsParticipated: '',
        shortlistedGroup: '',
        technicallyQualified: '',
        bidByQualifiedFirm: '',
        qualifiedFirmName: '',
        contractSignDate: '',
        procRemarks: '',
        vendor: '',
        amt: '',
        deliverySchedule: '',
        delivery: 'Pending',
        inspection: 'Pending',
        inspectionRemarks: '',
        payment: 'Pending',
        warranty: '',
        perfSecurityType: '',
        perfSecurity: '',
        perfSecurityRemarks: '',
        firm: '',
        contract: '',
        comm: '',
        totalRemuneration: '',
        totalReimbursable: '',
        paidAmount: '',
        paidRemuneration: '',
        paidReimbursable: '',
        paymentPct: '',
        paymentPctRemarks: '',
        insurance: 'Valid',
        insuranceType: '',
        insuranceName: '',
        insuranceIssuance: '',
        insuranceExpiry: '',
        insuranceRemarks: '',
        deliverables: [],
      });
    } else if (modal.type === 'procurement-edit' && modal.row) {
      const { row: storeRow } = resolveProcStoreRow(
        { procYetToInitiate, procInprocess, procOngoing, procCompleted, procCivil },
        modal.row,
        modal.index,
        modal.listKey as ProcListKey | null | undefined,
      );
      setForm({
        ...storeRow,
        currentStatus: storeRow.currentStatus || storeRow.status || PROC_STATUS.IN_PROCESS,
      });
    } else if (modal.row) {
      const initRow = (modal.type === 'training' || modal.type === 'training-edit') && modal.component === 1
        ? enrichCoeTrainingRow(modal.row)
        : modal.row;
      const extra = modal.type === 'procurement-yti' || modal.type === 'procurement-yti-add'
        ? {
            type: initRow.type || modal.user?.procScope?.[0] || 'Goods',
            status: initRow.status || PROC_STATUS.YET,
          }
        : (modal.type === 'training' || modal.type === 'training-edit') && modal.component === 2
          ? { type: normalizeC2TrainingType(initRow.type) }
          : {};
      setForm({ ...initRow, ...extra });
    } else if ((modal.type === 'training' || modal.type === 'training-edit') && modal.component === 1) {
      setForm({
        org: 'TEVTA',
        type: 'COE',
        div: '',
        dist: '',
        inst: '',
        course: '',
        dur: '6',
        batch: '',
        start: '',
        end: '',
        male: 0,
        female: 0,
        target: null,
        batchStatus: '',
        principalName: '',
        principalContact: '',
        passMale: null,
        passFemale: null,
        passTransMale: null,
        passTransFemale: null,
        placeMale: null,
        placeFemale: null,
      });
    } else if ((modal.type === 'training' || modal.type === 'training-edit') && modal.component === 2 && modal.type === 'training') {
      setForm({
        org: 'TEVTA',
        type: 'CBTA',
        div: '',
        dist: '',
        inst: '',
        course: '',
        dur: '6',
        start: '',
        end: '',
        male: 0,
        female: 0,
        target: null,
        passMale: null,
        passFemale: null,
        passTransMale: null,
        passTransFemale: null,
        principalName: '',
        principalContact: '',
      });
    } else {
      setForm({});
    }
  }, [modal, contractMgmt, procYetToInitiate, procInprocess, procOngoing, procCompleted, procCivil]);

  if (!modal) return null;

  const row = asProcRow(modal.row);
  const modalIndex = modal.index ?? null;
  const modalComponent = modal.component ?? 2;
  const modalCat = modal.cat;
  const modalUser = modal.user;

  const set = (key: string, val: unknown) => setForm((f) => ({ ...f, [key]: val }));

  const handleClose = () => {
    setForm({});
    closeModal();
  };

  const persistVersion = () => saveJson(STORAGE_KEYS.dataVersion, DATA_VERSION);

  const save = () => {
    if (user?.readOnly || modal.readOnly || modalUser?.readOnly) {
      showToast('⚠️', 'This account is view-only');
      return;
    }
    if (modal.type === 'civil' || modal.type === 'coe') {
      updateCivil(modalIndex ?? 0, { ...row, ...form });
      persistVersion();
      addAudit('Civil Works', `Updated: ${row.site || form.site}`);
      showToast('✅', 'Civil works record updated');
    } else if (modal.type === 'gap') {
      saveGap(modalIndex ?? null, {
        act: '', ind: '', resp: '', time: '', prog: '',
        ...row,
        ...form,
        status: normalizeGapStatus(String(form.status ?? row.status ?? 'Planned')),
      });
      showToast('✅', 'GAP activity saved');
    } else if (modal.type === 'budget') {
      saveBudgetRow(modalIndex ?? null, {
        code: '', acct: '', desc: '', alloc: 0, pnd: 0, fd: 0, util: 0, color: '#1565C0',
        ...row, ...form,
      });
      showToast('✅', 'Budget line saved');
    } else if (modal.type === 'training' || modal.type === 'training-edit') {
      const male = formNum(form, 'male');
      const female = formNum(form, 'female');
      const passCounts = buildGenderCounts(form, 'passMale', 'passFemale');
      const passTransMale = parseOptionalCount(form.passTransMale);
      const passTransFemale = parseOptionalCount(form.passTransFemale);
      const placeCounts = buildGenderCounts(form, 'placeMale', 'placeFemale');
      const hasPassDetail = passCounts.passMale != null
        || passCounts.passFemale != null
        || passTransMale != null
        || passTransFemale != null;
      const passTotal = !hasPassDetail
        ? null
        : (passCounts.passMale ?? 0)
          + (passCounts.passFemale ?? 0)
          + (passTransMale ?? 0)
          + (passTransFemale ?? 0);
      const placeTotal = placeCounts.placeMale == null && placeCounts.placeFemale == null
        ? null
        : (placeCounts.placeMale ?? 0) + (placeCounts.placeFemale ?? 0);
      const trainingRow: TrainingRow = {
        org: 'TEVTA',
        inst: '',
        course: '',
        div: '',
        dist: '',
        type: modalComponent === 1 ? 'COE' : 'Conventional',
        dur: '6',
        male: modalComponent === 1 ? male : 0,
        female: modalComponent === 1 ? female : 0,
        ...row,
        ...form,
        total: formNum(form, 'total') || male + female + (Number(form.transMale) || 0) + (Number(form.transFemale) || 0),
        passMale: passCounts.passMale,
        passFemale: passCounts.passFemale,
        passTransMale,
        passTransFemale,
        passTotal,
        placeMale: placeCounts.placeMale,
        placeFemale: placeCounts.placeFemale,
        placeTotal,
      };
      if (modalComponent === 1) {
        trainingRow.org = formStr(form, 'org') || 'TEVTA';
      }
      if (modalComponent === 2) {
        trainingRow.type = normalizeC2TrainingType(formStr(form, 'type') || trainingRow.type);
      }
      saveTrainingRow(modalComponent, modalIndex ?? null, trainingRow);
      persistVersion();
      showToast('✅', 'Training batch saved');
    } else if (modal.type === 'placement-add') {
      const placementRow = {
        dist: formStr(form, 'dist'),
        inst: formStr(form, 'inst'),
        course: formStr(form, 'course'),
        dur: formStr(form, 'dur'),
        start: formStr(form, 'start'),
        end: formStr(form, 'end'),
        traineeName: formStr(form, 'traineeName'),
        gender: formStr(form, 'gender'),
        traineeContact: formStr(form, 'traineeContact'),
        cnic: formStr(form, 'cnic'),
        employmentStatus: normalizePlacementEmploymentStatus(formStr(form, 'employmentStatus')),
        employerName: formStr(form, 'employerName'),
        salary: formStr(form, 'salary'),
        designation: formStr(form, 'designation'),
        employerContact: formStr(form, 'employerContact'),
        employerAddress: formStr(form, 'employerAddress'),
        div: formStr(form, 'div'),
      };
      if (!placementRow.traineeName.trim() || !placementRow.inst.trim()) {
        showToast('⚠️', 'Trainee name and institute are required');
        return;
      }
      if (modalComponent === 2) {
        useAppStore.getState().saveC2PlacementRow(null, placementRow);
      } else {
        useAppStore.getState().saveC1PlacementRow(null, placementRow);
      }
      showToast('✅', 'Placement proforma record added');
    } else if (modal.type === 'procurement') {
      const status = formStr(form, 'status') || PROC_STATUS.YET;
      if (!formStr(form, 'ref').trim() || !formStr(form, 'name').trim()) {
        showToast('⚠️', 'Reference and Package Name are required');
        return;
      }
      const ref = formStr(form, 'ref').trim();
      const id = `proc-${ref.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}`;
      const bidValidityDays = formStr(form, 'bidValidityDays');
      const bidByQualifiedFirm = formStr(form, 'bidByQualifiedFirm');
      const packageRow = {
        id,
        ref,
        name: formStr(form, 'name').trim(),
        type: formStr(form, 'type') || 'Goods',
        fy: formStr(form, 'fy') || '2025-26',
        party: formStr(form, 'party') || '',
        pkr: formNum(form, 'pkr'),
        usd: formStr(form, 'usd'),
        status,
        dateOfEntry: formStr(form, 'dateOfEntry'),
        remarks: formStr(form, 'remarks'),
        advertisedOn: formStr(form, 'advertisedOn'),
        ppRef: formStr(form, 'ppRef'),
        bidOpenDate: formStr(form, 'bidOpenDate'),
        bidValidityDays: bidValidityDays === '' ? '' : Number(bidValidityDays),
        bidValidityTill: formStr(form, 'bidValidityTill'),
        firmsParticipated: formStr(form, 'firmsParticipated'),
        shortlistedGroup: formStr(form, 'shortlistedGroup'),
        technicallyQualified: formStr(form, 'technicallyQualified'),
        bidByQualifiedFirm: bidByQualifiedFirm === '' ? '' : Number(bidByQualifiedFirm),
        qualifiedFirmName: formStr(form, 'qualifiedFirmName'),
        contractSignDate: formStr(form, 'contractSignDate'),
        procRemarks: formStr(form, 'procRemarks'),
        currentStatus: status === PROC_STATUS.ON_GOING
          ? 'Ongoing'
          : status === PROC_STATUS.IN_PROCESS
            ? 'In Process'
            : status,
      };
      addProcPackage(status, packageRow);
      if (status === PROC_STATUS.ON_GOING) {
        const cat = procCmCategory(packageRow.type);
        const cmBase = buildCmRowFromProc(packageRow);
        if (cat && cmBase) {
          const cmRow = cat === 'coe'
            ? {
                ...cmBase,
                procId: id,
                procRef: ref,
                vendor: formStr(form, 'vendor') || cmBase.vendor,
                amt: formStr(form, 'amt') || cmBase.amt,
                contract: formStr(form, 'contract') || cmBase.contract,
                po: formStr(form, 'ppRef') || cmBase.po,
                deliverySchedule: formStr(form, 'deliverySchedule'),
                delivery: formStr(form, 'delivery') || 'Pending',
                inspection: formStr(form, 'inspection') || 'Pending',
                inspectionRemarks: formStr(form, 'inspectionRemarks'),
                payment: formStr(form, 'payment') || 'Pending',
                warranty: formStr(form, 'warranty'),
                perfSecurityType: formStr(form, 'perfSecurityType'),
                perfSecurity: formStr(form, 'perfSecurity'),
                perfSecurityRemarks: formStr(form, 'perfSecurityRemarks'),
                remarks: formStr(form, 'procRemarks') || formStr(form, 'remarks'),
              }
            : {
                ...cmBase,
                procId: id,
                procRef: ref,
                firm: formStr(form, 'firm') || cmBase.firm,
                pkr: formNum(form, 'pkr'),
                usd: formStr(form, 'usd'),
                contract: formStr(form, 'contract') || cmBase.contract,
                comm: formStr(form, 'comm') || cmBase.comm,
                totalRemuneration: formStr(form, 'totalRemuneration'),
                totalReimbursable: formStr(form, 'totalReimbursable'),
                paidAmount: formStr(form, 'paidAmount'),
                paidRemuneration: formStr(form, 'paidRemuneration'),
                paidReimbursable: formStr(form, 'paidReimbursable'),
                paymentPct: formStr(form, 'paymentPct'),
                paymentPctRemarks: formStr(form, 'paymentPctRemarks'),
                insurance: formStr(form, 'insurance') || 'Valid',
                insuranceType: formStr(form, 'insuranceType'),
                insuranceName: formStr(form, 'insuranceName'),
                insuranceIssuance: formStr(form, 'insuranceIssuance'),
                insuranceExpiry: formStr(form, 'insuranceExpiry'),
                insuranceRemarks: formStr(form, 'insuranceRemarks'),
                deliverables: formDeliverables(form),
                remarks: formStr(form, 'procRemarks') || formStr(form, 'remarks'),
              };
          saveContractMgmt(
            cat,
            null,
            cat === 'coe' ? normalizeCmGoodsRow(cmRow) : normalizeCmSvcRow(cmRow),
            { name: 'Procurement Officer', role: 'procurement' },
          );
        }
      }
      pushProcHistory({
        action: 'Added procurement package',
        ref,
        type: packageRow.type,
        ...procHistoryActor({ name: 'Procurement Officer', role: 'procurement', roleLabel: 'Deputy Director Procurement & Contracts' }),
        from: '—',
        to: status,
      });
      showToast('✅', `Package added to ${status}`);
    } else if (modal.type === 'procurement-yti-add') {
      const actor = modalUser || { name: 'Officer', role: '' };
      const scope = actor.procScope || modalUser?.procScope || [];
      if (!formStr(form, 'ref').trim() || !formStr(form, 'name').trim()) {
        showToast('⚠️', 'Package Reference and Name are required');
        return;
      }
      const type = formStr(form, 'type') || scope[0] || 'Goods';
      if (scope.length && !scope.includes(type)) {
        showToast('⚠️', `You can only add ${scope.join(' or ')} packages`);
        return;
      }
      const ref = formStr(form, 'ref').trim();
      const id = `yti-${ref.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      addProcPackage('Yet to Initiate', {
        id,
        ref,
        name: formStr(form, 'name').trim(),
        type,
        usd: formNum(form, 'usd'),
        pkr: formNum(form, 'pkr'),
        dateOfEntry: formStr(form, 'dateOfEntry'),
        remarks: formStr(form, 'remarks'),
        status: PROC_STATUS.YET,
      });
      useAppStore.getState().pushProcHistory({
        action: 'Added Yet to Initiate package',
        ref,
        type,
        ...procHistoryActor(actor),
        from: '—',
        to: PROC_STATUS.YET,
      });
      showToast('✅', 'Package added to Yet to Initiate');
    } else if (modal.type === 'procurement-yti') {
      const actor = modalUser || { name: 'Officer', role: '' };
      const canEdit = !modal.readOnly || modal.allowProcurementPromote;
      if (!canEdit) {
        showToast('⚠️', 'Yet to Initiate records are updated by AD/DD officers only');
        return;
      }
      if (!formStr(form, 'ref').trim()) {
        showToast('⚠️', 'Package Reference is required');
        return;
      }
      const idx = useAppStore.getState().procYetToInitiate.findIndex((r) => r.id === row.id);
      if (formStr(form, 'status') === PROC_STATUS.IN_PROCESS) {
        const ok = promoteYtiToInProcess(String(row.id ?? ''), {
          ref: formStr(form, 'ref').trim(),
          dateOfEntry: formStr(form, 'dateOfEntry'),
          pkr: formNum(form, 'pkr'),
          usd: formNum(form, 'usd') || row.usd,
          remarks: formStr(form, 'remarks'),
        }, actor);
        if (!ok) {
          showToast('⚠️', 'Could not move package — record not found');
          return;
        }
        if (modal.allowProcurementPromote) setProcModTab('In Process');
        showToast('✅', 'Package moved to In Process');
      } else {
        saveProcYetToInitiate(idx, {
          ref: formStr(form, 'ref').trim(),
          dateOfEntry: formStr(form, 'dateOfEntry'),
          pkr: formNum(form, 'pkr'),
          usd: formNum(form, 'usd') || row.usd,
          remarks: formStr(form, 'remarks'),
        }, actor);
        showToast('✅', 'Yet to Initiate record saved');
      }
    } else if (modal.type === 'procurement-edit') {
      const actor = modalUser || { name: 'Procurement Officer', role: 'procurement', roleLabel: 'Deputy Director Procurement & Contracts' };
      if (modal.readOnly) {
        showToast('⚠️', 'This record cannot be edited from this view');
        return;
      }
      if (!formStr(form, 'ref').trim()) {
        showToast('⚠️', 'Package Reference is required');
        return;
      }
      const currentStatus = formStr(form, 'currentStatus') || 'In Process';
      if (currentStatus === PROC_STATUS.YET) {
        const ok = demoteInProcessToYti(String(row.id ?? ''), actor);
        if (!ok) {
          showToast('⚠️', 'Could not revert — record not found in In Process list');
          return;
        }
        showToast('✅', 'Package reverted to Yet to Initiate');
      } else {
        const bidValidityDays = formStr(form, 'bidValidityDays');
        const bidByQualifiedFirm = formStr(form, 'bidByQualifiedFirm');
        const ok = saveProcInprocess(modalIndex, {
          ...form,
          id: row.id,
          ref: formStr(form, 'ref').trim(),
          pkr: formNum(form, 'pkr'),
          usd: formStr(form, 'usd'),
          bidValidityDays: bidValidityDays === '' ? '' : Number(bidValidityDays),
          bidByQualifiedFirm: bidByQualifiedFirm === '' ? '' : Number(bidByQualifiedFirm),
          currentStatus,
        }, actor);
        if (!ok) {
          showToast('⚠️', 'Could not save — record not found in In Process list');
          return;
        }
        showToast('✅', currentStatus === 'Ongoing' ? 'Package moved to Contract Management' : 'In Process record saved');
      }
    } else if (modal.type === 'cm') {
      const cat = modalCat || 'coe';
      const actor = modalUser || { name: 'Contract Mgmt Officer', role: 'contractmgmt' };
      const payload = (cat === 'cons' || cat === 'nc')
        ? normalizeCmSvcRow(form)
        : normalizeCmGoodsRow(form);
      const { row: storeRow, index: storeIndex } = resolveCmStoreRow(
        contractMgmt,
        cat,
        modal.row,
        modalIndex,
      );
      const merged = mergeCmRow(storeRow, stripCmRowMeta(payload));
      saveContractMgmt(cat, storeIndex >= 0 ? storeIndex : modalIndex, merged, actor);
      showToast('✅', formStr(form, 'status') === 'Completed' ? 'Contract completed — moved to Completed Procurement' : 'Contract record saved');
    } else if (modal.type === 'cm-insurance') {
      const cat = modalCat || 'cons';
      const actor = modalUser || { name: 'Contract Mgmt Officer', role: 'contractmgmt' };
      const payload = normalizeCmSvcRow(form);
      const { row: storeRow, index: storeIndex } = resolveCmStoreRow(
        contractMgmt,
        cat,
        modal.row,
        modalIndex,
      );
      const merged = mergeCmRow(storeRow, stripCmRowMeta(payload));
      saveContractMgmt(cat, storeIndex >= 0 ? storeIndex : modalIndex, merged, actor);
      showToast('✅', modalCat === 'nc' ? 'Performance security details saved' : 'Insurance details saved');
    } else if (modal.type === 'c3-deliverable-detail') {
      if (modalIndex == null) {
        showToast('⚠️', 'Adding deliverables is disabled in this interface');
        return;
      }
      if (!formStr(form, 'title').trim()) {
        showToast('⚠️', 'Please enter deliverable title');
        return;
      }
      const title = formStr(form, 'title').trim();
      saveC3DeliverableDetail(modalIndex ?? null, {
        no: formNum(form, 'no') || 1,
        title,
        timeline: formStr(form, 'timeline').trim() || '',
        status: formStr(form, 'status'),
        remarks: formStr(form, 'remarks').trim() || '',
      });
      addAudit('Component III', `${modalIndex != null ? 'Updated' : 'Added'} deliverable: ${title}`);
      showToast('✅', 'Deliverable saved');
    } else if (modal.type === 'c3-deliverable-payment') {
      if (isC3PaymentLocked(asC3Row(modal.row))) {
        showToast('⚠️', 'Paid payment records cannot be changed');
        return;
      }
      const pct = formNum(form, 'pct');
      const net = formNum(form, 'net');
      if (!formStr(form, 'invoice').trim() && formStr(form, 'status') === 'Paid') {
        showToast('⚠️', 'Invoice number is required for paid records');
        return;
      }
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        showToast('⚠️', 'Percentage must be between 0 and 100');
        return;
      }
      if (Number.isNaN(net) || net < 0) {
        showToast('⚠️', 'Net amount must be zero or greater');
        return;
      }
      const receivedRaw = form.received;
      saveC3DeliverablePayment(modalIndex ?? null, {
        no: formNum(form, 'no') || 1,
        invoice: formStr(form, 'invoice').trim() || '',
        pct,
        net,
        invoiceDate: formStr(form, 'invoiceDate').trim() || '',
        receiptDate: formStr(form, 'receiptDate').trim() || '',
        status: formStr(form, 'status'),
        received: receivedRaw === '' || receivedRaw == null ? null : Number(receivedRaw),
      });
      addAudit('Component III', `${modalIndex != null ? 'Updated' : 'Added'} payment #${formNum(form, 'no')}`);
      showToast('✅', 'Payment record saved');
    } else if (modal.type === 'c3-expense') {
      const monthKey = formStr(form, 'expenseMonthKey') || (typeof modal.month === 'string' ? modal.month : '');
      if (!monthKey) {
        showToast('⚠️', 'Please select a month for this expense entry');
        return;
      }
      const headRaw = formStr(form, 'head').trim();
      const isNewHead = modalIndex == null && headRaw === '__new__';
      const head = isNewHead ? formStr(form, 'newHead').trim() : headRaw;
      if (!head) {
        showToast('⚠️', isNewHead ? 'Please enter a name for the new expense head' : 'Please select an expense head');
        return;
      }
      if (isNewHead && !formNum(form, 'totalAmount')) {
        showToast('⚠️', 'Please enter total budget amount for the new expense head');
        return;
      }
      const expenses = ((component3?.expenses as Record<string, unknown>[]) || []);
      let saveIndex = modalIndex != null && modalIndex >= 0 ? modalIndex : expenses.findIndex((e) => String(e.head) === head);
      const baseRow = saveIndex >= 0 ? asC3Row(expenses[saveIndex]) : asC3Row(row);
      const rowMonths = (baseRow.months ?? {}) as Record<string, number | string>;
      const nextVal = Number(formMonths(form)[monthKey]) || 0;
      if (nextVal < 0) {
        showToast('⚠️', 'Amount must be zero or greater');
        return;
      }
      const mergedMonths = { ...rowMonths, [monthKey]: nextVal };
      const rec = c3RecalcExpense({
        ...baseRow,
        head,
        qty: isNewHead ? (formNum(form, 'qty') || 1) : (Number(baseRow.qty) || formNum(form, 'qty') || 1),
        totalAmount: isNewHead ? formNum(form, 'totalAmount') : (Number(baseRow.totalAmount) || formNum(form, 'totalAmount') || 0),
        months: mergedMonths,
      });
      saveC3Expense(saveIndex >= 0 ? saveIndex : null, rec);
      const monthLabel = SEED.COMPONENT3_MONTHS.find((m) => m.key === monthKey)?.label || monthKey;
      addAudit('Component III', `${saveIndex >= 0 ? 'Updated' : 'Added'} expense: ${head} (${monthLabel})`);
      showToast('✅', 'Expense entry saved');
    } else if (modal.type === 'c3-contingency') {
      if (!formStr(form, 'description').trim()) {
        showToast('⚠️', 'Please enter description');
        return;
      }
      const contingencyAmount = formNum(form, 'contingencyAmount');
      const invoiceValue = formNum(form, 'invoiceValue');
      if (Number.isNaN(contingencyAmount) || contingencyAmount < 0) {
        showToast('⚠️', 'Contingency amount must be zero or greater');
        return;
      }
      if (Number.isNaN(invoiceValue) || invoiceValue < 0) {
        showToast('⚠️', 'Invoice value must be zero or greater');
        return;
      }
      saveC3Contingency(modalIndex ?? null, {
        sr: formNum(form, 'sr') || (modalIndex != null ? modalIndex + 1 : ((component3.contingencies as unknown[])?.length || 0) + 1),
        contingencyAmount,
        description: formStr(form, 'description').trim(),
        invoiceDate: formStr(form, 'invoiceDate').trim(),
        invoiceValue,
        status: formStr(form, 'status') || 'Unpaid',
        remarks: formStr(form, 'remarks').trim(),
        letterRef: formStr(form, 'letterRef').trim(),
        letterDate: formStr(form, 'letterDate').trim(),
        approvalDate: formStr(form, 'approvalDate').trim(),
      });
      addAudit('Component III', `${modalIndex != null ? 'Updated' : 'Added'} contingency record`);
      showToast('✅', 'Contingency record saved');
    } else if (modal.type === 'c3-provisional-sum') {
      if (!formStr(form, 'description').trim()) {
        showToast('⚠️', 'Please enter description');
        return;
      }
      const invoiceValue = formNum(form, 'invoiceValue');
      if (Number.isNaN(invoiceValue) || invoiceValue < 0) {
        showToast('⚠️', 'Invoice value must be zero or greater');
        return;
      }
      saveC3ProvisionalSum(modalIndex ?? null, {
        description: formStr(form, 'description').trim(),
        invoiceDate: formStr(form, 'invoiceDate').trim(),
        invoiceValue,
        status: formStr(form, 'status') || 'Unpaid',
        remarks: formStr(form, 'remarks').trim(),
        letterRef: formStr(form, 'letterRef').trim(),
        letterDate: formStr(form, 'letterDate').trim(),
        approvalDate: formStr(form, 'approvalDate').trim(),
      });
      addAudit('Component III', `${modalIndex != null ? 'Updated' : 'Added'} provisional sum record`);
      showToast('✅', 'Provisional sum record saved');
    }
    handleClose();
  };

  const setMonth = (key: string, val: string | number) => {
    setForm((f) => ({
      ...f,
      months: { ...(f.months || {}), [key]: Number(val) || 0 },
    }));
  };

  const procViewMode = modal.type === 'procurement-edit' && (
    modal.readOnly
    || formStr(form, 'status') === PROC_STATUS.COMPLETED
    || formStr(form, 'status') === PROC_STATUS.ON_GOING
  );

  const c3ExpenseMonthKey = modal.type === 'c3-expense'
    ? (formStr(form, 'expenseMonthKey') || (typeof modal.month === 'string' ? modal.month : ''))
    : '';
  const c3ExpenseMonthLabel = c3ExpenseMonthKey
    ? (SEED.COMPONENT3_MONTHS.find((m) => m.key === c3ExpenseMonthKey)?.label || c3ExpenseMonthKey)
    : '';

  const titles: Record<string, string> = {
    civil: 'Civil Works Progress Update',
    coe: 'Update COE Status',
    gap: modalIndex != null ? 'Update GAP Activity' : 'Add GAP Activity',
    budget: modalIndex != null ? 'Edit Budget Line' : 'Add Budget Line',
    training: modalComponent === 1 ? 'Add Institute Record' : 'Add Training Record',
    'training-edit': modalComponent === 1 ? 'Edit Institute Record' : 'Edit Training Record',
    procurement: 'Add Procurement Package',
    'procurement-yti': modal.allowProcurementPromote
      ? 'Update Package — Promote to In Process'
      : modal.readOnly
        ? 'View Yet to Initiate Package'
        : 'Edit Yet to Initiate Package',
    'procurement-yti-add': 'Add Yet to Initiate Package',
    'procurement-edit': procViewMode
      ? `View ${formStr(form, 'status') || 'Procurement'} Package`
      : 'Edit In Process Package',
    cm: modalIndex != null ? 'Edit Contract Record' : 'Add Contract Record',
    'cm-insurance': modalCat === 'nc' ? 'Performance Security Details' : 'Insurance Details',
    'c3-deliverable-detail': modalIndex != null ? 'Update Deliverable' : 'Add Deliverable',
    'c3-deliverable-payment': modalIndex != null ? 'Update Payment Record' : 'Add Payment Record',
    'c3-expense': modalIndex != null ? 'Update Expense Entry' : 'Add Expense Entry',
    'c3-contingency': modalIndex != null ? 'Update Contingency' : 'Add Contingency',
    'c3-provisional-sum': modalIndex != null ? 'Update Provisional Sum' : 'Add Provisional Sums',
    'placement-add': 'Add Placement Proforma',
  };

  const isCoeTraining = (modal.type === 'training' || modal.type === 'training-edit') && modalComponent === 1;
  const isC3 = modal.type?.startsWith('c3-');
  const isCmCompleted = modal.type === 'cm' && formStr(form, 'status') === 'Completed';
  const cmInsuranceViewOnly = Boolean(modal.readOnly || modalUser?.readOnly || user?.readOnly);
  const c3PaymentLocked = modal.type === 'c3-deliverable-payment' && isC3PaymentLocked(asC3Row(modal.row));
  const c3ExpenseHeads = c3ExpenseHeadOptions((component3?.expenses ?? []) as C3FormRow[]);
  const c3ExpenseHeadRaw = modal.type === 'c3-expense' ? formStr(form, 'head') : '';
  const c3ExpenseIsNewHead = modal.type === 'c3-expense' && modalIndex == null && c3ExpenseHeadRaw === '__new__';
  const readOnlyStyle = { background: '#F9FAFB' };
  const procAddStatus = modal.type === 'procurement' ? (formStr(form, 'status') || PROC_STATUS.YET) : '';
  const procAddShowInProcess = procAddStatus === PROC_STATUS.IN_PROCESS
    || procAddStatus === PROC_STATUS.ON_GOING
    || procAddStatus === PROC_STATUS.COMPLETED;
  const procAddShowOngoing = procAddStatus === PROC_STATUS.ON_GOING;
  const procAddType = formStr(form, 'type') || 'Goods';

  return (
    <Modal
      title={titles[modal.type] || 'Form'}
      wide={modal.type === 'civil' || modal.type === 'training' || modal.type === 'training-edit' || isC3 || modal.type === 'procurement-edit' || modal.type === 'procurement' || modal.type === 'cm' || modal.type === 'cm-insurance'}
      xwide={modal.type === 'cm'}
      onClose={handleClose}
    >
      {(modal.type === 'civil' || modal.type === 'coe') && (
        <>
          <div className="form-grid cols1">
            <Field label="Select COE Site">
              <select
                value={String(form.sr ?? (modalIndex != null ? modalIndex + 1 : 1))}
                onChange={(e) => {
                  const idx = Number(e.target.value) - 1;
                  const c = civil[idx];
                  if (c) setForm({ ...c });
                }}
              >
                {civil.map((c, ci) => (
                  <option key={String(c.sr ?? ci)} value={String(c.sr ?? ci + 1)}>{String(c.site ?? '')} ({String(c.region ?? '')})</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="form-grid">
            <Field label="Physical Progress — Planned (%)">
              <input type="number" min={0} max={100} value={formField(form, 'phy_plan')} onChange={(e) => set('phy_plan', +e.target.value)} />
            </Field>
            <Field label="Physical Progress — Actual (%)">
              <input type="number" min={0} max={100} value={formField(form, 'phy_act')} onChange={(e) => set('phy_act', +e.target.value)} />
            </Field>
            <Field label="Financial Progress — Planned (%)">
              <input type="number" min={0} max={100} value={formField(form, 'fin_plan')} onChange={(e) => set('fin_plan', +e.target.value)} />
            </Field>
            <Field label="Financial Progress — Actual (%)">
              <input type="number" min={0} max={100} value={formField(form, 'fin_act')} onChange={(e) => set('fin_act', +e.target.value)} />
            </Field>
          </div>
          <div className="form-grid cols3">
            <Field label="Structure (%)">
              <input type="number" min={0} max={100} value={formField(form, 'structure')} onChange={(e) => set('structure', +e.target.value)} />
            </Field>
            <Field label="MEP (%)">
              <input type="number" min={0} max={100} value={formField(form, 'mep')} onChange={(e) => set('mep', +e.target.value)} />
            </Field>
            <Field label="Finishing (%)">
              <input type="number" min={0} max={100} value={formField(form, 'finish')} onChange={(e) => set('finish', +e.target.value)} />
            </Field>
          </div>
          <div className="form-grid">
            <Field label="Status">
              <select value={formField(form, 'status') || 'In Progress'} onChange={(e) => set('status', e.target.value)}>
                <option value="Completed">Completed</option>
                <option value="In Progress">In Progress (On Track)</option>
                <option value="Pending">Pending (At Risk)</option>
              </select>
            </Field>
          </div>
          <div className="form-grid cols1">
            <Field label="Remarks / Notes">
              <textarea value={formField(form, 'notes') || ''} onChange={(e) => set('notes', e.target.value)} placeholder="Any site-specific notes, issues, or milestones achieved…" />
            </Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} audit="Entry logged with timestamp · Admin Hassan" />
        </>
      )}

      {modal.type === 'gap' && (
        <>
          <div className="form-grid">
            <Field label={GAP_COLUMNS.act}>
              <textarea rows={2} value={formField(form, 'act') || ''} onChange={(e) => set('act', e.target.value)} />
            </Field>
            <Field label={GAP_COLUMNS.ind}>
              <textarea rows={3} value={formField(form, 'ind') || ''} onChange={(e) => set('ind', e.target.value)} />
            </Field>
            <Field label={GAP_COLUMNS.resp}>
              <input type="text" value={formField(form, 'resp') || ''} onChange={(e) => set('resp', e.target.value)} />
            </Field>
            <Field label={GAP_COLUMNS.time}>
              <input type="text" value={formField(form, 'time') || ''} onChange={(e) => set('time', e.target.value)} />
            </Field>
            <Field label={GAP_COLUMNS.prog}>
              <textarea rows={4} value={formField(form, 'prog') || ''} onChange={(e) => set('prog', e.target.value)} />
            </Field>
            <Field label={GAP_COLUMNS.status}>
              <select value={formField(form, 'status') || ''} onChange={(e) => set('status', e.target.value)}>
                <option value="">—</option>
                {GAP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                {formField(form, 'status') && !GAP_STATUSES.includes(formField(form, 'status') as typeof GAP_STATUSES[number]) && (
                  <option value={formField(form, 'status')}>{formField(form, 'status')}</option>
                )}
              </select>
            </Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} />
        </>
      )}

      {modal.type === 'budget' && (
        <>
          <div className="form-grid">
            <Field label="Object Code"><input type="text" value={formField(form, 'code') || ''} onChange={(e) => set('code', e.target.value)} /></Field>
            <Field label="Account Details"><input type="text" value={formField(form, 'acct') || ''} onChange={(e) => set('acct', e.target.value)} /></Field>
            <Field label="Description"><input type="text" value={formField(form, 'desc') || ''} onChange={(e) => set('desc', e.target.value)} /></Field>
            <Field label="Allocation 2025-26 (M)"><input type="number" value={formField(form, 'alloc')} onChange={(e) => set('alloc', +e.target.value)} /></Field>
            <Field label="Released by PnD (M)"><input type="number" value={formField(form, 'pnd')} onChange={(e) => set('pnd', +e.target.value)} /></Field>
            <Field label="Released by FD (M)"><input type="number" value={formField(form, 'fd')} onChange={(e) => set('fd', +e.target.value)} /></Field>
            <Field label="Utilization to Date (M)"><input type="number" value={formField(form, 'util')} onChange={(e) => set('util', +e.target.value)} /></Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} />
        </>
      )}

      {(modal.type === 'training' || modal.type === 'training-edit') && isCoeTraining && (
        <>
          <div className="form-section-label">Institute &amp; Course Details</div>
          <div className="form-grid">
            {modal.type === 'training-edit' && form.sr != null && (
              <Field label="S.#">
                <input type="number" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'sr')} />
              </Field>
            )}
            <Field label="Division"><input type="text" value={formField(form, 'div') || ''} onChange={(e) => set('div', e.target.value)} /></Field>
            <Field label="District"><input type="text" value={formField(form, 'dist') || ''} onChange={(e) => set('dist', e.target.value)} /></Field>
            <Field label="Organization"><input type="text" value={formField(form, 'org') || 'TEVTA'} onChange={(e) => set('org', e.target.value)} /></Field>
            <Field label="COE Name"><input type="text" value={formField(form, 'inst') || ''} onChange={(e) => set('inst', e.target.value)} /></Field>
            <Field label="Course Name"><input type="text" value={formField(form, 'course') || formField(form, 'courseName')} onChange={(e) => set('course', e.target.value)} /></Field>
            <Field label="Course Duration (months)"><input type="text" value={formField(form, 'dur')} onChange={(e) => set('dur', e.target.value)} /></Field>
            <Field label="Batch"><input type="text" value={formField(form, 'batch') || ''} onChange={(e) => set('batch', e.target.value)} placeholder="e.g. Batch-I" /></Field>
            <Field label="Course Start Date"><input type="date" value={formField(form, 'start') || ''} onChange={(e) => set('start', e.target.value)} /></Field>
            <Field label="Course End Date"><input type="date" value={formField(form, 'end') || ''} onChange={(e) => set('end', e.target.value)} /></Field>
            <Field label="Target">
              <input
                type="number"
                min={0}
                readOnly={modal.type === 'training-edit'}
                style={modal.type === 'training-edit' ? { background: '#F9FAFB' } : undefined}
                value={formField(form, 'target')}
                onChange={(e) => set('target', e.target.value === '' ? null : +e.target.value)}
              />
            </Field>
            <Field label="Status">
              <select value={formField(form, 'batchStatus') || ''} onChange={(e) => set('batchStatus', e.target.value)}>
                <option value="">—</option>
                <option>Completed</option>
                <option>In Process</option>
                <option>Assessment pending</option>
                <option>Result awaited</option>
              </select>
            </Field>
          </div>
          <div className="form-section-label">Enrolled</div>
          <div className="form-grid cols3">
            <Field label="Male"><input type="number" min={0} value={formField(form, 'male')} onChange={(e) => set('male', e.target.value === '' ? 0 : +e.target.value)} /></Field>
            <Field label="Female"><input type="number" min={0} value={formField(form, 'female')} onChange={(e) => set('female', e.target.value === '' ? 0 : +e.target.value)} /></Field>
            <Field label="Transgender Male"><input type="number" min={0} value={formField(form, 'transMale')} onChange={(e) => set('transMale', e.target.value === '' ? 0 : +e.target.value)} /></Field>
            <Field label="Transgender Female"><input type="number" min={0} value={formField(form, 'transFemale')} onChange={(e) => set('transFemale', e.target.value === '' ? 0 : +e.target.value)} /></Field>
            <Field label="Total (auto)"><input type="number" readOnly style={{ background: '#F9FAFB' }} value={(Number(form.male) || 0) + (Number(form.female) || 0) + (Number(form.transMale) || 0) + (Number(form.transFemale) || 0)} /></Field>
          </div>
          <div className="form-section-label">Passout</div>
          <div className="form-grid cols3">
            <Field label="Male"><input type="number" min={0} value={formField(form, 'passMale')} onChange={(e) => set('passMale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Female"><input type="number" min={0} value={formField(form, 'passFemale')} onChange={(e) => set('passFemale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Transgender male"><input type="number" min={0} value={formField(form, 'passTransMale')} onChange={(e) => set('passTransMale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Transgender female"><input type="number" min={0} value={formField(form, 'passTransFemale')} onChange={(e) => set('passTransFemale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Total (auto)"><input type="number" readOnly style={{ background: '#F9FAFB' }} value={form.passMale == null && form.passFemale == null && form.passTransMale == null && form.passTransFemale == null ? '' : (parseOptionalCount(form.passMale) ?? 0) + (parseOptionalCount(form.passFemale) ?? 0) + (parseOptionalCount(form.passTransMale) ?? 0) + (parseOptionalCount(form.passTransFemale) ?? 0)} /></Field>
          </div>
          <div className="form-section-label">Principal</div>
          <div className="form-grid">
            <Field label="Principal Name"><input type="text" value={formField(form, 'principalName') || ''} onChange={(e) => set('principalName', e.target.value)} /></Field>
            <Field label="Principal Contact"><input type="text" value={formField(form, 'principalContact') || ''} onChange={(e) => set('principalContact', e.target.value)} placeholder="e.g. 0300-1234567" /></Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} />
        </>
      )}

      {(modal.type === 'training' || modal.type === 'training-edit') && !isCoeTraining && (
        <>
          <div className="form-grid">
            <Field label="Organisation">
              <select value={formField(form, 'org') || 'TEVTA'} onChange={(e) => set('org', e.target.value)}>
                <option value="TEVTA">TEVTA</option>
                <option value="PVTC">PVTC</option>
                <option value="Private">Private</option>
              </select>
            </Field>
            <Field label="Division"><input type="text" value={formField(form, 'div') || ''} onChange={(e) => set('div', e.target.value)} /></Field>
            <Field label="District"><input type="text" value={formField(form, 'dist') || ''} onChange={(e) => set('dist', e.target.value)} /></Field>
            <Field label="Institute"><input type="text" value={formField(form, 'inst') || ''} onChange={(e) => set('inst', e.target.value)} /></Field>
            <Field label="Course"><input type="text" value={formField(form, 'course') || ''} onChange={(e) => set('course', e.target.value)} /></Field>
            <Field label="Duration (months)"><input type="number" value={formField(form, 'dur') || ''} onChange={(e) => set('dur', e.target.value)} /></Field>
            <Field label="Training Type">
              <select
                value={normalizeC2TrainingType(formField(form, 'type') || 'CBTA')}
                onChange={(e) => set('type', e.target.value)}
              >
                <option value="CBTA">CBTA</option>
                <option value="Conventional">Conventional</option>
              </select>
            </Field>
            <Field label="Start Date"><input type="date" value={formField(form, 'start') || ''} onChange={(e) => set('start', e.target.value)} /></Field>
            <Field label="End Date"><input type="date" value={formField(form, 'end') || ''} onChange={(e) => set('end', e.target.value)} /></Field>
            <Field label="Target"><input type="number" min={0} value={formField(form, 'target')} onChange={(e) => set('target', e.target.value === '' ? null : +e.target.value)} /></Field>
          </div>
          <div className="form-section-label">Enrolled</div>
          <div className="form-grid cols3">
            <Field label="Male Enrolled"><input type="number" min={0} value={formField(form, 'male')} onChange={(e) => set('male', +e.target.value)} /></Field>
            <Field label="Female Enrolled"><input type="number" min={0} value={formField(form, 'female')} onChange={(e) => set('female', +e.target.value)} /></Field>
            <Field label="Transgender Male"><input type="number" min={0} value={formField(form, 'transMale')} onChange={(e) => set('transMale', +e.target.value)} /></Field>
            <Field label="Transgender Female"><input type="number" min={0} value={formField(form, 'transFemale')} onChange={(e) => set('transFemale', +e.target.value)} /></Field>
            <Field label="Total Enrolled (auto)"><input type="number" readOnly style={{ background: '#F9FAFB' }} value={(Number(form.male) || 0) + (Number(form.female) || 0) + (Number(form.transMale) || 0) + (Number(form.transFemale) || 0)} /></Field>
          </div>
          <div className="form-section-label">Pass-Out</div>
          <div className="form-grid cols3">
            <Field label="Male Pass-Out"><input type="number" min={0} value={formField(form, 'passMale')} onChange={(e) => set('passMale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Female Pass-Out"><input type="number" min={0} value={formField(form, 'passFemale')} onChange={(e) => set('passFemale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Transgender male"><input type="number" min={0} value={formField(form, 'passTransMale')} onChange={(e) => set('passTransMale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Transgender female"><input type="number" min={0} value={formField(form, 'passTransFemale')} onChange={(e) => set('passTransFemale', e.target.value === '' ? null : +e.target.value)} /></Field>
            <Field label="Total Pass-Out (auto)"><input type="number" readOnly style={{ background: '#F9FAFB' }} value={form.passMale == null && form.passFemale == null && form.passTransMale == null && form.passTransFemale == null ? '' : String((parseOptionalCount(form.passMale) ?? 0) + (parseOptionalCount(form.passFemale) ?? 0) + (parseOptionalCount(form.passTransMale) ?? 0) + (parseOptionalCount(form.passTransFemale) ?? 0) || formNum(form, 'passTotal') || '')} /></Field>
          </div>
          <div className="form-section-label">Principal</div>
          <div className="form-grid">
            <Field label="Principal"><input type="text" value={formField(form, 'principalName') || ''} onChange={(e) => set('principalName', e.target.value)} /></Field>
            <Field label="Contact"><input type="text" value={formField(form, 'principalContact') || ''} onChange={(e) => set('principalContact', e.target.value)} /></Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} />
        </>
      )}

      {modal.type === 'placement-add' && (
        <>
          <div className="form-section-label">Institute &amp; Course</div>
          <div className="form-grid">
            <Field label="District"><input type="text" value={formField(form, 'dist') || ''} onChange={(e) => set('dist', e.target.value)} /></Field>
            <Field label="Name of Institute"><input type="text" value={formField(form, 'inst') || ''} onChange={(e) => set('inst', e.target.value)} /></Field>
            <Field label="Name of Course"><input type="text" value={formField(form, 'course') || ''} onChange={(e) => set('course', e.target.value)} /></Field>
            <Field label="Duration (months)"><input type="text" value={formField(form, 'dur') || ''} onChange={(e) => set('dur', e.target.value)} /></Field>
            <Field label="Start Date"><input type="date" value={formField(form, 'start') || ''} onChange={(e) => set('start', e.target.value)} /></Field>
            <Field label="End Date"><input type="date" value={formField(form, 'end') || ''} onChange={(e) => set('end', e.target.value)} /></Field>
          </div>
          <div className="form-section-label">Trainee</div>
          <div className="form-grid">
            <Field label="Name of Trainee"><input type="text" value={formField(form, 'traineeName') || ''} onChange={(e) => set('traineeName', e.target.value)} /></Field>
            <Field label="Gender M/F">
              <select value={formField(form, 'gender') || ''} onChange={(e) => set('gender', e.target.value)}>
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Transgender Male">Transgender Male</option>
                <option value="Transgender Female">Transgender Female</option>
              </select>
            </Field>
            <Field label="Contact No. of Trainee"><input type="text" value={formField(form, 'traineeContact') || ''} onChange={(e) => set('traineeContact', e.target.value)} /></Field>
            <Field label="CNIC No."><input type="text" value={formField(form, 'cnic') || ''} onChange={(e) => set('cnic', e.target.value)} /></Field>
          </div>
          <div className="form-section-label">Employment</div>
          <div className="form-grid">
            <Field label="Employment Status" style={{ gridColumn: '1 / -1' }}>
              <select value={formField(form, 'employmentStatus') || ''} onChange={(e) => set('employmentStatus', e.target.value)}>
                <option value="">—</option>
                {PLACEMENT_EMPLOYMENT_STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </Field>
            <Field label="Name of Industry/Company"><input type="text" value={formField(form, 'employerName') || ''} onChange={(e) => set('employerName', e.target.value)} /></Field>
            <Field label="Monthly Salary/Income"><input type="text" value={formField(form, 'salary') || ''} onChange={(e) => set('salary', e.target.value)} /></Field>
            <Field label="Designation"><input type="text" value={formField(form, 'designation') || ''} onChange={(e) => set('designation', e.target.value)} /></Field>
            <Field label="Contact No. of Industry/Company"><input type="text" value={formField(form, 'employerContact') || ''} onChange={(e) => set('employerContact', e.target.value)} /></Field>
            <Field label="Address of Industry/Company" style={{ gridColumn: '1 / -1' }}>
              <textarea value={formField(form, 'employerAddress') || ''} onChange={(e) => set('employerAddress', e.target.value)} rows={2} />
            </Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} />
        </>
      )}

      {modal.type === 'procurement' && (
        <>
          <div className="form-grid">
            <Field label="Reference"><input type="text" value={formField(form, 'ref') || ''} onChange={(e) => set('ref', e.target.value)} /></Field>
            <Field label="Package Name"><input type="text" value={formField(form, 'name') || ''} onChange={(e) => set('name', e.target.value)} /></Field>
            <Field label="Type">
              <select value={formField(form, 'type') || 'Goods'} onChange={(e) => set('type', e.target.value)}>
                <option>Goods</option><option>Consultancy</option><option>Non-Consultancy</option><option>Civil Works</option>
              </select>
            </Field>
            <Field label="Financial Year"><input type="text" value={formField(form, 'fy') || '2025-26'} onChange={(e) => set('fy', e.target.value)} /></Field>
            <Field label="Party / Contractor"><input type="text" value={formField(form, 'party') || ''} onChange={(e) => set('party', e.target.value)} /></Field>
            <Field label="Amount (PKR)"><input type="number" value={formField(form, 'pkr')} onChange={(e) => set('pkr', +e.target.value)} /></Field>
            <Field label="Amount (USD)"><input type="number" value={formField(form, 'usd')} onChange={(e) => set('usd', +e.target.value)} /></Field>
            <Field label="Status">
              <select value={formField(form, 'status') || PROC_STATUS.YET} onChange={(e) => set('status', e.target.value)}>
                <option value={PROC_STATUS.YET}>Yet to Initiate</option>
                <option value={PROC_STATUS.IN_PROCESS}>In Process</option>
                <option value={PROC_STATUS.ON_GOING}>On Going</option>
                <option value={PROC_STATUS.COMPLETED}>Completed</option>
              </select>
            </Field>
            {procAddStatus === PROC_STATUS.YET && (
              <>
                <Field label="Date of Entry"><input type="date" value={formField(form, 'dateOfEntry') || ''} onChange={(e) => set('dateOfEntry', e.target.value)} /></Field>
                <Field label="Remarks" style={{ gridColumn: '1 / -1' }}>
                  <textarea value={formField(form, 'remarks') || ''} onChange={(e) => set('remarks', e.target.value)} rows={2} />
                </Field>
              </>
            )}
          </div>

          {procAddShowInProcess && (
            <>
              <div className="cm-form-sec-title" style={{ marginTop: 8 }}>In Process Details</div>
              <div className="form-grid">
                <Field label="Advertised On Date"><input type="date" value={formField(form, 'advertisedOn') || ''} onChange={(e) => set('advertisedOn', e.target.value)} /></Field>
                <Field label="PP Reference"><input type="text" value={formField(form, 'ppRef') || ''} onChange={(e) => set('ppRef', e.target.value)} /></Field>
                <Field label="Proposal / Bid Open Date"><input type="date" value={formField(form, 'bidOpenDate') || ''} onChange={(e) => set('bidOpenDate', e.target.value)} /></Field>
                <Field label="Bid Validity Days"><input type="number" min={0} value={formField(form, 'bidValidityDays')} onChange={(e) => set('bidValidityDays', e.target.value)} /></Field>
                <Field label="Bid Validity Till"><input type="date" value={formField(form, 'bidValidityTill') || ''} onChange={(e) => set('bidValidityTill', e.target.value)} /></Field>
                <Field label="Firms Participated" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'firmsParticipated') || ''} onChange={(e) => set('firmsParticipated', e.target.value)} rows={2} placeholder="One firm per line" /></Field>
                <Field label="Officially Shortlisted Group" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'shortlistedGroup') || ''} onChange={(e) => set('shortlistedGroup', e.target.value)} rows={2} /></Field>
                <Field label="Technically Qualified Firms" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'technicallyQualified') || ''} onChange={(e) => set('technicallyQualified', e.target.value)} rows={2} placeholder="One firm per line" /></Field>
                <Field label="Bid by Qualified Firm (Value)"><input type="number" value={formField(form, 'bidByQualifiedFirm')} onChange={(e) => set('bidByQualifiedFirm', e.target.value)} /></Field>
                <Field label="Qualified Firm Name"><input type="text" value={formField(form, 'qualifiedFirmName') || ''} onChange={(e) => set('qualifiedFirmName', e.target.value)} /></Field>
                <Field label="Contract Sign Date"><input type="date" value={formField(form, 'contractSignDate') || ''} onChange={(e) => set('contractSignDate', e.target.value)} /></Field>
                <Field label="Remarks" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'procRemarks') || formField(form, 'remarks')} onChange={(e) => set('procRemarks', e.target.value)} rows={3} /></Field>
              </div>
            </>
          )}

          {procAddShowOngoing && isProcGoodsType(procAddType) && (
            <>
              <div className="cm-form-sec-title" style={{ marginTop: 8 }}>Ongoing Contract — Goods</div>
              <div className="form-grid">
                <Field label="Vendor"><input type="text" value={formField(form, 'vendor') || ''} onChange={(e) => set('vendor', e.target.value)} /></Field>
                <Field label="Amount"><input type="text" value={formField(form, 'amt') || ''} onChange={(e) => set('amt', e.target.value)} /></Field>
                <Field label="Delivery Schedule" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'deliverySchedule') || ''} onChange={(e) => set('deliverySchedule', e.target.value)} rows={2} placeholder="Timelines and dates" /></Field>
                <Field label="Delivery Status">
                  <select value={formField(form, 'delivery') || 'Pending'} onChange={(e) => set('delivery', e.target.value)}>
                    <option>Pending</option><option>Delivered</option>
                  </select>
                </Field>
                <Field label="Inspection Status">
                  <select value={formField(form, 'inspection') || 'Pending'} onChange={(e) => set('inspection', e.target.value)}>
                    <option>Pending</option><option>Inspected</option>
                  </select>
                </Field>
                <Field label="Inspection Remarks"><input type="text" value={formField(form, 'inspectionRemarks') || ''} onChange={(e) => set('inspectionRemarks', e.target.value)} /></Field>
                <Field label="Payment Status">
                  <select value={formField(form, 'payment') || 'Pending'} onChange={(e) => set('payment', e.target.value)}>
                    <option>Pending</option><option>Paid</option>
                  </select>
                </Field>
                <Field label="Warranty Remarks"><input type="text" value={formField(form, 'warranty') || ''} onChange={(e) => set('warranty', e.target.value)} /></Field>
                <CmPerfSecurityFields form={form} set={set} formField={formField} />
                {isCmBankGuaranteeType(formField(form, 'perfSecurityType')) && (
                  <Field label="Remarks" style={{ gridColumn: '1 / -1' }}>
                    <textarea
                      value={formField(form, 'perfSecurityRemarks') || ''}
                      onChange={(e) => set('perfSecurityRemarks', e.target.value)}
                      rows={2}
                      placeholder="Bank guarantee remarks"
                    />
                  </Field>
                )}
              </div>
            </>
          )}

          {procAddShowOngoing && isProcServicesType(procAddType) && (
            <>
              <div className="cm-form-sec-title" style={{ marginTop: 8 }}>Ongoing Contract — {procAddType}</div>
              <div className="form-grid">
                <CmServicesFormSection form={form} set={set} setForm={setForm} />
              </div>
            </>
          )}

          <FormFooter
            onClose={handleClose}
            onSave={save}
            audit={procAddShowOngoing
              ? 'On Going packages are also linked to Contract Management'
              : procAddShowInProcess
                ? 'In Process fields are saved with the new package'
                : 'Default status is Yet to Initiate'}
          />
        </>
      )}

      {modal.type === 'procurement-yti-add' && (
        <>
          <div className="form-grid">
            <Field label="Package Reference"><input type="text" value={formField(form, 'ref') || ''} onChange={(e) => set('ref', e.target.value)} /></Field>
            <Field label="Date of Entry"><input type="date" value={formField(form, 'dateOfEntry') || ''} onChange={(e) => set('dateOfEntry', e.target.value)} /></Field>
            <Field label="Package Name" style={{ gridColumn: '1 / -1' }}><input type="text" value={formField(form, 'name') || ''} onChange={(e) => set('name', e.target.value)} /></Field>
            <Field label="Type">
              <select value={formField(form, 'type') || modalUser?.procScope?.[0] || 'Goods'} onChange={(e) => set('type', e.target.value)}>
                {(modalUser?.procScope?.length ? modalUser.procScope : ['Goods', 'Consultancy', 'Non-Consultancy']).map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Amount (USD)"><input type="number" value={formField(form, 'usd')} onChange={(e) => set('usd', +e.target.value)} /></Field>
            <Field label="Amount (PKR)"><input type="number" value={formField(form, 'pkr')} onChange={(e) => set('pkr', +e.target.value)} /></Field>
            <Field label="Remarks" style={{ gridColumn: '1 / -1' }}>
              <textarea value={formField(form, 'remarks') || ''} onChange={(e) => set('remarks', e.target.value)} rows={2} />
            </Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} audit="New package logged with transaction history" />
        </>
      )}

      {modal.type === 'procurement-yti' && (() => {
        const canEdit = !modal.readOnly || modal.allowProcurementPromote;
        return (
        <>
          <div className="form-grid">
            <Field label="Package Reference"><input type="text" readOnly={!canEdit} value={formField(form, 'ref') || ''} onChange={(e) => set('ref', e.target.value)} /></Field>
            <Field label="Date of Entry"><input type="date" readOnly={!canEdit} value={formField(form, 'dateOfEntry') || ''} onChange={(e) => set('dateOfEntry', e.target.value)} /></Field>
            <Field label="Package Name" style={{ gridColumn: '1 / -1' }}><input type="text" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'name') || ''} /></Field>
            <Field label="Type"><input type="text" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'type') || ''} /></Field>
            <Field label="Amount (USD)"><input type="number" readOnly={!canEdit} value={formField(form, 'usd')} onChange={(e) => set('usd', +e.target.value)} /></Field>
            <Field label="Amount (PKR)"><input type="number" readOnly={!canEdit} value={formField(form, 'pkr')} onChange={(e) => set('pkr', +e.target.value)} /></Field>
            {canEdit && (
              <Field label="Status">
                <select value={formField(form, 'status') || PROC_STATUS.YET} onChange={(e) => set('status', e.target.value)}>
                  <option value={PROC_STATUS.YET}>Yet to Initiate</option>
                  <option value={PROC_STATUS.IN_PROCESS}>In Process</option>
                </select>
              </Field>
            )}
            <Field label="Remarks" style={{ gridColumn: '1 / -1' }}>
              <textarea readOnly={!canEdit} value={formField(form, 'remarks') || ''} onChange={(e) => set('remarks', e.target.value)} rows={2} />
            </Field>
          </div>
          {!canEdit && (
            <ProcActivityHistorySection history={packageActivityHistory(asProcRow(row))} />
          )}
          <FormFooter
            onClose={handleClose}
            onSave={canEdit ? save : undefined}
            audit={modal.allowProcurementPromote ? 'Set Status to In Process to move to In Process tab' : 'Changes logged with transaction history'}
          />
        </>
        );
      })()}

      {modal.type === 'procurement-edit' && procViewMode && (
        <>
          <ProcPackageDetailView procRow={form} contractMgmt={contractMgmt} />
          <FormFooter onClose={handleClose} audit="Shows In Process fields plus Contract Management fields saved when marked Completed" />
        </>
      )}

      {modal.type === 'procurement-edit' && !procViewMode && (
        <>
          <div className="form-grid">
            <Field label="Type"><input type="text" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'type') || ''} /></Field>
            <Field label="Package Reference"><input type="text" value={formField(form, 'ref') || ''} onChange={(e) => set('ref', e.target.value)} /></Field>
            <Field label="Package Name" style={{ gridColumn: '1 / -1' }}><input type="text" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'name') || ''} /></Field>
            <Field label="Budget Amount (PKR)"><input type="number" value={formField(form, 'pkr')} onChange={(e) => set('pkr', +e.target.value)} /></Field>
            <Field label="Budget Amount (USD)"><input type="number" value={formField(form, 'usd')} onChange={(e) => set('usd', +e.target.value)} /></Field>
            <Field label="Advertised On Date"><input type="date" value={formField(form, 'advertisedOn') || ''} onChange={(e) => set('advertisedOn', e.target.value)} /></Field>
            <Field label="PP Reference"><input type="text" value={formField(form, 'ppRef') || ''} onChange={(e) => set('ppRef', e.target.value)} /></Field>
            <Field label="Proposal / Bid Open Date"><input type="date" value={formField(form, 'bidOpenDate') || ''} onChange={(e) => set('bidOpenDate', e.target.value)} /></Field>
            <Field label="Bid Validity Days"><input type="number" min={0} value={formField(form, 'bidValidityDays')} onChange={(e) => set('bidValidityDays', e.target.value)} /></Field>
            <Field label="Bid Validity Till"><input type="date" value={formField(form, 'bidValidityTill') || ''} onChange={(e) => set('bidValidityTill', e.target.value)} /></Field>
            <Field label="Firms Participated" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'firmsParticipated') || ''} onChange={(e) => set('firmsParticipated', e.target.value)} rows={2} placeholder="One firm per line" /></Field>
            <Field label="Officially Shortlisted Group" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'shortlistedGroup') || ''} onChange={(e) => set('shortlistedGroup', e.target.value)} rows={2} /></Field>
            <Field label="Technically Qualified Firms" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'technicallyQualified') || ''} onChange={(e) => set('technicallyQualified', e.target.value)} rows={2} placeholder="One firm per line" /></Field>
            <Field label="Bid by Qualified Firm (Value)"><input type="number" value={formField(form, 'bidByQualifiedFirm')} onChange={(e) => set('bidByQualifiedFirm', e.target.value)} /></Field>
            <Field label="Qualified Firm Name"><input type="text" value={formField(form, 'qualifiedFirmName') || ''} onChange={(e) => set('qualifiedFirmName', e.target.value)} /></Field>
            <Field label="Contract Sign Date"><input type="date" value={formField(form, 'contractSignDate') || ''} onChange={(e) => set('contractSignDate', e.target.value)} /></Field>
            <Field label="Current Status">
              <select value={formField(form, 'currentStatus') || 'In Process'} onChange={(e) => set('currentStatus', e.target.value)}>
                <option value="In Process">In Process</option>
                <option value={PROC_STATUS.YET}>Yet to Initiate</option>
                <option value="Ongoing">Ongoing</option>
              </select>
            </Field>
            <Field label="Remarks" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'procRemarks') || formField(form, 'remarks')} onChange={(e) => set('procRemarks', e.target.value)} rows={3} /></Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} audit="Set Current Status to Ongoing to send to Contract Management, or Yet to Initiate to revert" />
        </>
      )}

      {modal.type === 'cm' && (
        <>
          <div className="form-grid">
            <Field label="Package Name"><input type="text" value={formField(form, 'name') || ''} onChange={(e) => set('name', e.target.value)} /></Field>
            <Field label="Procurement Ref"><input type="text" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'procRef') || '—'} /></Field>
            {(modalCat === 'piu' || modalCat === 'coe' || !modalCat) && !isCmCompleted && (
              <>
                <Field label="Vendor"><input type="text" value={formField(form, 'vendor') || ''} onChange={(e) => set('vendor', e.target.value)} /></Field>
                <Field label="Amount"><input type="text" value={formField(form, 'amt') || ''} onChange={(e) => set('amt', e.target.value)} /></Field>
                <Field label="Delivery Schedule" style={{ gridColumn: '1 / -1' }}><textarea value={formField(form, 'deliverySchedule') || ''} onChange={(e) => set('deliverySchedule', e.target.value)} rows={2} placeholder="Timelines and dates" /></Field>
                <Field label="Delivery Status">
                  <select value={formField(form, 'delivery') || 'Pending'} onChange={(e) => set('delivery', e.target.value)}>
                    <option>Pending</option><option>Delivered</option>
                  </select>
                </Field>
                <Field label="Inspection Status">
                  <select value={formField(form, 'inspection') || 'Pending'} onChange={(e) => set('inspection', e.target.value)}>
                    <option>Pending</option><option>Inspected</option>
                  </select>
                </Field>
                <Field label="Inspection Remarks"><input type="text" value={formField(form, 'inspectionRemarks') || ''} onChange={(e) => set('inspectionRemarks', e.target.value)} /></Field>
                <Field label="Payment Status">
                  <select value={formField(form, 'payment') || 'Pending'} onChange={(e) => set('payment', e.target.value)}>
                    <option>Pending</option><option>Paid</option>
                  </select>
                </Field>
                <Field label="Warranty Remarks"><input type="text" value={formField(form, 'warranty') || ''} onChange={(e) => set('warranty', e.target.value)} /></Field>
                {modalCat === 'coe' && (
                  <CmPerfSecurityFields form={form} set={set} formField={formField} />
                )}
              </>
            )}
            {(modalCat === 'cons' || modalCat === 'nc') && !isCmCompleted && (
              <CmServicesFormSection form={form} set={set} setForm={setForm} />
            )}
            {(modalCat === 'cons' || modalCat === 'nc') && (
              <CmPerfSecurityFields form={form} set={set} formField={formField} />
            )}
            {(modalCat === 'piu' || modalCat === 'cons' || modalCat === 'nc') && (
              <Field label="Remarks" style={{ gridColumn: '1 / -1' }}>
                <textarea value={formField(form, 'remarks') || ''} onChange={(e) => set('remarks', e.target.value)} rows={2} />
              </Field>
            )}
            <Field label="Current Status">
              <select value={formField(form, 'status') || 'On Going'} onChange={(e) => set('status', e.target.value)}>
                <option>On Going</option><option>Completed</option>
              </select>
            </Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} audit="Completed status moves record to Completed Procurement tab" />
        </>
      )}

      {modal.type === 'cm-insurance' && (
        <>
          <div className="form-grid">
            <Field label="Package"><input type="text" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'name') || '—'} /></Field>
            <Field label="Procurement Ref"><input type="text" readOnly style={{ background: '#F9FAFB' }} value={formField(form, 'procRef') || '—'} /></Field>
            {modalCat === 'nc' ? (
              <CmPerfSecurityFields form={form} set={set} formField={formField} />
            ) : (
              <>
            <Field label="Insurance Status">
              <select
                value={formField(form, 'insurance') || 'Valid'}
                disabled={cmInsuranceViewOnly}
                style={cmInsuranceViewOnly ? readOnlyStyle : undefined}
                onChange={(e) => set('insurance', e.target.value)}
              >
                <option>Valid</option>
                <option>Expired</option>
              </select>
            </Field>
            <Field label="Insurance Type">
              <select
                value={formField(form, 'insuranceType') || ''}
                disabled={cmInsuranceViewOnly}
                style={cmInsuranceViewOnly ? readOnlyStyle : undefined}
                onChange={(e) => set('insuranceType', e.target.value)}
              >
                <option value="">— Select type —</option>
                {CM_INSURANCE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
                {formField(form, 'insuranceType')
                  && !CM_INSURANCE_TYPES.includes(formField(form, 'insuranceType') as typeof CM_INSURANCE_TYPES[number]) && (
                  <option value={formField(form, 'insuranceType')}>{formField(form, 'insuranceType')}</option>
                )}
              </select>
            </Field>
            <Field label="Insurance Name"><input type="text" readOnly={cmInsuranceViewOnly} style={cmInsuranceViewOnly ? readOnlyStyle : undefined} value={formField(form, 'insuranceName') || ''} onChange={(e) => set('insuranceName', e.target.value)} /></Field>
            <Field label="Date of Issuance"><input type="date" readOnly={cmInsuranceViewOnly} style={cmInsuranceViewOnly ? readOnlyStyle : undefined} value={formField(form, 'insuranceIssuance') || ''} onChange={(e) => set('insuranceIssuance', e.target.value)} /></Field>
            <Field label="Date of Expiry">
              <input type="date" readOnly={cmInsuranceViewOnly} style={cmInsuranceViewOnly ? readOnlyStyle : undefined} value={formField(form, 'insuranceExpiry') || ''} onChange={(e) => set('insuranceExpiry', e.target.value)} />
              {fmtCmInsuranceExpiry(formField(form, 'insuranceExpiry')) && (
                <span className={`cm-bg-expiry-hint cm-bg-expiry-hint--${fmtCmInsuranceExpiry(formField(form, 'insuranceExpiry')) === 'Expired' ? 'expired' : 'expiring'}`}>
                  {fmtCmInsuranceExpiry(formField(form, 'insuranceExpiry'))}
                </span>
              )}
            </Field>
            <Field label="Remarks" style={{ gridColumn: '1 / -1' }}><textarea readOnly={cmInsuranceViewOnly} style={cmInsuranceViewOnly ? readOnlyStyle : undefined} value={formField(form, 'insuranceRemarks') || ''} onChange={(e) => set('insuranceRemarks', e.target.value)} rows={3} /></Field>
              </>
            )}
          </div>
          <FormFooter
            onClose={handleClose}
            onSave={cmInsuranceViewOnly ? undefined : save}
            audit={cmInsuranceViewOnly
              ? `View-only — ${modalCat === 'nc' ? 'performance security' : 'insurance'} details cannot be changed`
              : `${modalCat === 'nc' ? 'Performance security' : 'Insurance'} details saved on the contract record`}
          />
        </>
      )}

      {modal.type === 'c3-deliverable-detail' && (
        <>
          <div className="form-grid">
            <Field label="Deliverable No.">
              <input type="number" min={1} readOnly style={readOnlyStyle} value={formField(form, 'no')} />
            </Field>
            <Field label="Title" className="fg2" style={{ gridColumn: '1 / -1' }}>
              <input type="text" value={formField(form, 'title') || ''} onChange={(e) => set('title', e.target.value)} placeholder="Deliverable title" />
            </Field>
            <Field label="Timeline of submission" className="fg2" style={{ gridColumn: '1 / -1' }}>
              <input type="text" value={formField(form, 'timeline') || ''} onChange={(e) => set('timeline', e.target.value)} placeholder="e.g. 12 Weeks after Signing of Contract" />
            </Field>
            <Field label="Status">
              <select value={formField(form, 'status') || ''} onChange={(e) => set('status', e.target.value)}>
                <option value="">—</option>
                <option value="Paid">Approved</option>
                <option value="Outstanding">Pending/Review In Process</option>
              </select>
            </Field>
            <Field label="Remarks" className="fg2" style={{ gridColumn: '1 / -1' }}>
              <textarea value={formField(form, 'remarks') || ''} onChange={(e) => set('remarks', e.target.value)} rows={3} placeholder="Notes" />
            </Field>
          </div>
          <FormFooter onClose={handleClose} onSave={save} audit="Logged with audit trail" />
        </>
      )}

      {modal.type === 'c3-deliverable-payment' && (
        <>
          {c3PaymentLocked && (
            <div className="c3-form-lock-note">This paid payment record is locked and cannot be modified.</div>
          )}
          <div className="form-grid">
            <Field label="Deliverable No.">
              <input type="number" min={1} readOnly style={readOnlyStyle} value={formField(form, 'no')} />
            </Field>
            <Field label="Invoice No."><input type="text" readOnly={c3PaymentLocked} style={c3PaymentLocked ? readOnlyStyle : undefined} value={formField(form, 'invoice') || ''} onChange={(e) => set('invoice', e.target.value)} placeholder="PIU-ADB-R1001" /></Field>
            <Field label="Remuneration (%)"><input type="number" min={0} max={100} step={0.1} readOnly={c3PaymentLocked} style={c3PaymentLocked ? readOnlyStyle : undefined} value={formField(form, 'pct')} onChange={(e) => set('pct', +e.target.value)} /></Field>
            <Field label="Invoice Net Amount (PKR)"><input type="number" min={0} readOnly={c3PaymentLocked} style={c3PaymentLocked ? readOnlyStyle : undefined} value={formField(form, 'net')} onChange={(e) => set('net', +e.target.value)} /></Field>
            <Field label="Payment Received (PKR)"><input type="number" min={0} readOnly={c3PaymentLocked} style={c3PaymentLocked ? readOnlyStyle : undefined} value={formField(form, 'received')} onChange={(e) => set('received', e.target.value === '' ? '' : +e.target.value)} placeholder="Leave empty if none" /></Field>
            <Field label="Status">
              <select value={formField(form, 'status') || ''} disabled={c3PaymentLocked} style={c3PaymentLocked ? readOnlyStyle : undefined} onChange={(e) => set('status', e.target.value)}>
                <option value="">—</option>
                <option value="Paid">Only Paid</option>
                <option value="Outstanding">Outstanding Deliverable</option>
              </select>
            </Field>
            <Field label="Invoice Date"><input type="text" readOnly={c3PaymentLocked} style={c3PaymentLocked ? readOnlyStyle : undefined} value={formField(form, 'invoiceDate') || ''} onChange={(e) => set('invoiceDate', e.target.value)} placeholder="e.g. 15-May-25" /></Field>
            <Field label="Date of Receipt"><input type="text" readOnly={c3PaymentLocked} style={c3PaymentLocked ? readOnlyStyle : undefined} value={formField(form, 'receiptDate') || ''} onChange={(e) => set('receiptDate', e.target.value)} placeholder="e.g. 4-Jun-25" /></Field>
          </div>
          <FormFooter onClose={handleClose} onSave={c3PaymentLocked ? undefined : save} audit="PRA @ 5% calculated automatically" />
        </>
      )}

      {modal.type === 'c3-expense' && (
        <>
          <div className="c3-form-lock-note" style={{ background: '#EFF6FF', borderColor: '#BFDBFE', color: '#1E40AF' }}>
            {modalIndex != null
              ? 'Update the amount for the selected month on this expense head.'
              : 'Choose a month, then select an existing expense head or add a new one with its monthly amount.'}
          </div>
          <div className="form-grid">
            <Field label="Date (Month)" className="fg2" style={{ gridColumn: '1 / -1' }}>
              <select
                value={c3ExpenseMonthKey}
                onChange={(e) => set('expenseMonthKey', e.target.value)}
              >
                <option value="">— Select month —</option>
                {SEED.COMPONENT3_MONTHS.map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Expense Head" className="fg2" style={{ gridColumn: '1 / -1' }}>
              <select
                value={c3ExpenseHeadRaw}
                disabled={modalIndex != null}
                style={modalIndex != null ? readOnlyStyle : undefined}
                onChange={(e) => {
                  const head = e.target.value;
                  set('head', head);
                  if (modalIndex == null && head && head !== '__new__') {
                    const expenses = (component3?.expenses as Record<string, unknown>[]) || [];
                    const existing = expenses.find((ex) => String(ex.head) === head);
                    if (existing) {
                      setForm((f) => ({
                        ...f,
                        head,
                        qty: existing.qty ?? 1,
                        ceiling: existing.ceiling ?? 0,
                        totalAmount: existing.totalAmount ?? 0,
                        months: { ...((existing.months as Record<string, unknown>) || {}) },
                      }));
                    }
                  } else if (head === '__new__') {
                    setForm((f) => ({
                      ...f,
                      head: '__new__',
                      newHead: '',
                      qty: 1,
                      ceiling: 0,
                      totalAmount: 0,
                      months: {},
                    }));
                  }
                }}
              >
                <option value="">— Select expense head —</option>
                {modalIndex == null && <option value="__new__">+ Add new expense head</option>}
                {c3ExpenseHeads.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </Field>
            {c3ExpenseIsNewHead && (
              <Field label="New Expense Head Name" className="fg2" style={{ gridColumn: '1 / -1' }}>
                <input
                  type="text"
                  value={formField(form, 'newHead') || ''}
                  onChange={(e) => set('newHead', e.target.value)}
                  placeholder="e.g. Travel & Logistics"
                />
              </Field>
            )}
            <Field label="Months">
              <input
                type="number"
                min={0}
                readOnly={modalIndex != null || (!c3ExpenseIsNewHead && !!c3ExpenseHeadRaw && c3ExpenseHeadRaw !== '__new__')}
                style={(modalIndex != null || (!c3ExpenseIsNewHead && !!c3ExpenseHeadRaw && c3ExpenseHeadRaw !== '__new__')) ? readOnlyStyle : undefined}
                value={formField(form, 'qty')}
                onChange={(e) => set('qty', +e.target.value)}
              />
            </Field>
            <Field label="Ceiling Amount (PKR) — auto">
              <input
                type="number"
                readOnly
                style={readOnlyStyle}
                value={formNum(form, 'qty') ? Math.round(formNum(form, 'totalAmount') / formNum(form, 'qty')) : formField(form, 'ceiling')}
              />
            </Field>
            <Field label="Total Amount (PKR)">
              <input
                type="number"
                min={0}
                readOnly={modalIndex != null || (!c3ExpenseIsNewHead && !!c3ExpenseHeadRaw && c3ExpenseHeadRaw !== '__new__')}
                style={(modalIndex != null || (!c3ExpenseIsNewHead && !!c3ExpenseHeadRaw && c3ExpenseHeadRaw !== '__new__')) ? readOnlyStyle : undefined}
                value={formField(form, 'totalAmount')}
                onChange={(e) => set('totalAmount', +e.target.value)}
              />
            </Field>
          </div>
          {c3ExpenseMonthKey ? (
            <div className="form-grid cols1">
              <Field label={`Amount for ${c3ExpenseMonthLabel} (PKR)`}>
                <input
                  type="number"
                  min={0}
                  value={String(formMonths(form)[c3ExpenseMonthKey] ?? '')}
                  onChange={(e) => setMonth(c3ExpenseMonthKey, e.target.value)}
                />
              </Field>
            </div>
          ) : (
            <div className="c3-form-lock-note">Select a month to enter the expense amount.</div>
          )}
          <FormFooter onClose={handleClose} onSave={c3ExpenseMonthKey ? save : undefined} audit="Ceiling, paid & remaining auto-calculated" />
        </>
      )}

      {modal.type === 'c3-contingency' && (() => {
        const projected = projectC3ContingencyStats(
          component3 as Record<string, unknown>,
          modalIndex,
          formNum(form, 'invoiceValue'),
          formStr(form, 'status') || 'Unpaid',
        );
        return (
          <>
            <div className="form-section-label">Contingency Summary — auto</div>
            <div className="form-grid">
              <Field label="Total Contingency Amount">
                <input type="text" readOnly style={readOnlyStyle} value={formatPkr(projected.totalAmount)} />
              </Field>
              <Field label="Used (Paid)">
                <input type="text" readOnly style={readOnlyStyle} value={formatPkr(projected.used)} />
              </Field>
              <Field label="Remaining">
                <input type="text" readOnly style={readOnlyStyle} value={formatPkr(projected.remaining)} />
              </Field>
              <Field label="Utilization">
                <input type="text" readOnly style={readOnlyStyle} value={`${projected.pct}%`} />
              </Field>
            </div>
            <div className="form-section-label">Contingency Entry</div>
            <div className="form-grid">
              <Field label="Sr.#">
                <input type="number" min={1} value={formField(form, 'sr')} onChange={(e) => set('sr', +e.target.value)} />
              </Field>
              <Field label="Contingency Amount (PKR)">
                <input type="number" min={0} value={formField(form, 'contingencyAmount')} onChange={(e) => set('contingencyAmount', +e.target.value)} />
              </Field>
              <Field label="Description" style={{ gridColumn: '1 / -1' }}>
                <input type="text" value={formField(form, 'description') || ''} onChange={(e) => set('description', e.target.value)} />
              </Field>
              <Field label="Invoice Date">
                <input type="date" value={formField(form, 'invoiceDate') || ''} onChange={(e) => set('invoiceDate', e.target.value)} />
              </Field>
              <Field label="Invoice Value (PKR)">
                <input type="number" min={0} value={formField(form, 'invoiceValue')} onChange={(e) => set('invoiceValue', +e.target.value)} />
              </Field>
              <Field label="Status">
                <select value={formField(form, 'status') || 'Unpaid'} onChange={(e) => set('status', e.target.value)}>
                  <option value="Paid">Paid</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </Field>
              <Field label="Remarks" style={{ gridColumn: '1 / -1' }}>
                <textarea value={formField(form, 'remarks') || ''} onChange={(e) => set('remarks', e.target.value)} rows={2} />
              </Field>
              <Field label="Letter Ref">
                <input type="text" value={formField(form, 'letterRef') || ''} onChange={(e) => set('letterRef', e.target.value)} />
              </Field>
              <Field label="Letter Date">
                <input type="date" value={formField(form, 'letterDate') || ''} onChange={(e) => set('letterDate', e.target.value)} />
              </Field>
              <Field label="Date of Approval">
                <input type="date" value={formField(form, 'approvalDate') || ''} onChange={(e) => set('approvalDate', e.target.value)} />
              </Field>
            </div>
            <FormFooter onClose={handleClose} onSave={save} audit="Totals update when you save this entry" />
          </>
        );
      })()}

      {modal.type === 'c3-provisional-sum' && (() => {
        const projected = projectC3ProvisionalSumStats(
          component3 as Record<string, unknown>,
          modalIndex,
          formNum(form, 'invoiceValue'),
          formStr(form, 'status') || 'Unpaid',
        );
        return (
          <>
            <div className="form-section-label">Provisional Sum Summary — auto</div>
            <div className="form-grid">
              <Field label="Total Provisional Sum">
                <input type="text" readOnly style={readOnlyStyle} value={formatPkr(projected.totalAmount)} />
              </Field>
              <Field label="Used (Paid)">
                <input type="text" readOnly style={readOnlyStyle} value={formatPkr(projected.used)} />
              </Field>
              <Field label="Remaining">
                <input type="text" readOnly style={readOnlyStyle} value={formatPkr(projected.remaining)} />
              </Field>
              <Field label="Utilization">
                <input type="text" readOnly style={readOnlyStyle} value={`${projected.pct}%`} />
              </Field>
            </div>
            <div className="form-section-label">Provisional Sum Entry</div>
            <div className="form-grid">
              <Field label="Description" style={{ gridColumn: '1 / -1' }}>
                <input type="text" value={formField(form, 'description') || ''} onChange={(e) => set('description', e.target.value)} />
              </Field>
              <Field label="Invoice Date">
                <input type="date" value={formField(form, 'invoiceDate') || ''} onChange={(e) => set('invoiceDate', e.target.value)} />
              </Field>
              <Field label="Invoice Value (PKR)">
                <input type="number" min={0} value={formField(form, 'invoiceValue')} onChange={(e) => set('invoiceValue', +e.target.value)} />
              </Field>
              <Field label="Status">
                <select value={formField(form, 'status') || 'Unpaid'} onChange={(e) => set('status', e.target.value)}>
                  <option value="Paid">Paid</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </Field>
              <Field label="Remarks" style={{ gridColumn: '1 / -1' }}>
                <textarea value={formField(form, 'remarks') || ''} onChange={(e) => set('remarks', e.target.value)} rows={2} />
              </Field>
              <Field label="Letter Ref">
                <input type="text" value={formField(form, 'letterRef') || ''} onChange={(e) => set('letterRef', e.target.value)} />
              </Field>
              <Field label="Letter Date">
                <input type="date" value={formField(form, 'letterDate') || ''} onChange={(e) => set('letterDate', e.target.value)} />
              </Field>
              <Field label="Date of Approval">
                <input type="date" value={formField(form, 'approvalDate') || ''} onChange={(e) => set('approvalDate', e.target.value)} />
              </Field>
            </div>
            <FormFooter onClose={handleClose} onSave={save} audit="Totals update when you save this entry" />
          </>
        );
      })()}
    </Modal>
  );
}
